import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Stack,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Sensors as SensorsIcon,
  CheckCircle as OnlineIcon,
  Cancel as OfflineIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { iotDevicesService, IoTDevice } from '../services/iot.service';
import { format } from 'date-fns';

const IoTDevicesManagement: React.FC = () => {
  const [devices, setDevices] = useState<IoTDevice[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDevice, setEditingDevice] = useState<IoTDevice | null>(null);
  const [filterActive, setFilterActive] = useState<boolean | undefined>(undefined);

  const [formData, setFormData] = useState({
    device_id: '',
    device_name: '',
    device_type: '',
    mqtt_topic_prefix: '',
    location: '',
    machine: '',
    is_active: true,
    configuration: '',
  });

  useEffect(() => {
    fetchDevices();
    fetchDeviceTypes();
  }, [filterActive]);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const response = await iotDevicesService.getAll({
        is_active: filterActive,
      });
      setDevices(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch devices');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeviceTypes = async () => {
    try {
      const response = await iotDevicesService.getDeviceTypes();
      setDeviceTypes(response.data);
    } catch (err) {
      console.error('Failed to fetch device types:', err);
    }
  };

  const handleOpenDialog = (device?: IoTDevice) => {
    if (device) {
      setEditingDevice(device);
      setFormData({
        device_id: device.device_id,
        device_name: device.device_name,
        device_type: device.device_type,
        mqtt_topic_prefix: device.mqtt_topic_prefix,
        location: device.location || '',
        machine: device.machine || '',
        is_active: device.is_active,
        configuration: device.configuration ? JSON.stringify(device.configuration, null, 2) : '',
      });
    } else {
      setEditingDevice(null);
      setFormData({
        device_id: '',
        device_name: '',
        device_type: '',
        mqtt_topic_prefix: '',
        location: '',
        machine: '',
        is_active: true,
        configuration: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingDevice(null);
    setError(null);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Parse configuration JSON if provided
      let configuration = null;
      if (formData.configuration.trim()) {
        try {
          configuration = JSON.parse(formData.configuration);
        } catch (e) {
          setError('Invalid JSON in configuration field');
          setLoading(false);
          return;
        }
      }

      const data = {
        device_id: formData.device_id,
        device_name: formData.device_name,
        device_type: formData.device_type,
        mqtt_topic_prefix: formData.mqtt_topic_prefix,
        location: formData.location || undefined,
        machine: formData.machine || undefined,
        is_active: formData.is_active,
        configuration,
      };

      if (editingDevice) {
        await iotDevicesService.update(editingDevice.id, data);
        setSuccess('Device updated successfully');
      } else {
        await iotDevicesService.create(data);
        setSuccess('Device created successfully');
      }

      handleCloseDialog();
      fetchDevices();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save device');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this device?')) {
      return;
    }

    try {
      setLoading(true);
      await iotDevicesService.delete(id);
      setSuccess('Device deleted successfully');
      fetchDevices();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete device');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (lastSeen?: string) => {
    if (!lastSeen) return 'default';
    const lastSeenDate = new Date(lastSeen);
    const now = new Date();
    const diffMinutes = (now.getTime() - lastSeenDate.getTime()) / 1000 / 60;
    
    if (diffMinutes < 5) return 'success';
    if (diffMinutes < 30) return 'warning';
    return 'error';
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h4" component="h1">
            <SensorsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            IoT Devices Management
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchDevices}
              sx={{ mr: 1 }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Device
            </Button>
          </Box>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Devices
                </Typography>
                <Typography variant="h4">
                  {devices.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Active Devices
                </Typography>
                <Typography variant="h4" color="success.main">
                  {devices.filter(d => d.is_active).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Online Now
                </Typography>
                <Typography variant="h4" color="primary">
                  {devices.filter(d => getStatusColor(d.last_seen) === 'success').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Device Types
                </Typography>
                <Typography variant="h4">
                  {new Set(devices.map(d => d.device_type)).size}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filter */}
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={filterActive === true}
                onChange={(e) => setFilterActive(e.target.checked ? true : undefined)}
              />
            }
            label="Show Active Only"
          />
        </Box>

        {/* Devices Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Device ID</TableCell>
                <TableCell>Device Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Machine</TableCell>
                <TableCell>Last Seen</TableCell>
                <TableCell>Total Batches</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No devices found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                devices.map((device) => (
                  <TableRow key={device.id}>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Chip
                          icon={device.is_active ? <OnlineIcon /> : <OfflineIcon />}
                          label={device.is_active ? 'Active' : 'Inactive'}
                          color={device.is_active ? 'success' : 'default'}
                          size="small"
                        />
                        {device.last_seen && (
                          <Chip
                            label="●"
                            size="small"
                            color={getStatusColor(device.last_seen)}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {device.device_id}
                      </Typography>
                    </TableCell>
                    <TableCell>{device.device_name}</TableCell>
                    <TableCell>
                      <Chip label={device.device_type} size="small" />
                    </TableCell>
                    <TableCell>{device.location || '-'}</TableCell>
                    <TableCell>{device.machine || '-'}</TableCell>
                    <TableCell>
                      {device.last_seen
                        ? format(new Date(device.last_seen), 'MMM dd, HH:mm:ss')
                        : 'Never'}
                    </TableCell>
                    <TableCell>{device.total_batches || 0}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(device)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(device.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingDevice ? 'Edit Device' : 'Add New Device'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Device ID"
                    value={formData.device_id}
                    onChange={(e) => setFormData({ ...formData, device_id: e.target.value })}
                    required
                    disabled={!!editingDevice}
                    helperText="Unique identifier for the device"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Device Name"
                    value={formData.device_name}
                    onChange={(e) => setFormData({ ...formData, device_name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Device Type"
                    value={formData.device_type}
                    onChange={(e) => setFormData({ ...formData, device_type: e.target.value })}
                    required
                  >
                    {deviceTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="MQTT Topic Prefix"
                    value={formData.mqtt_topic_prefix}
                    onChange={(e) => setFormData({ ...formData, mqtt_topic_prefix: e.target.value })}
                    required
                    helperText="e.g., oceanlanka/width"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Machine"
                    value={formData.machine}
                    onChange={(e) => setFormData({ ...formData, machine: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      />
                    }
                    label="Active"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Configuration (JSON)"
                    value={formData.configuration}
                    onChange={(e) => setFormData({ ...formData, configuration: e.target.value })}
                    multiline
                    rows={4}
                    placeholder='{"target_width": 2.64, "tolerance": 0.1}'
                    helperText="Optional JSON configuration for the device"
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" disabled={loading}>
              {editingDevice ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default IoTDevicesManagement;
